import React, { useRef, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';

import { emailConfirmation, regenerateConfirmation } from '../../api/confirmation';
import { getAdminAccessLevel, seesAmounts } from '../../utils/adminSession';
import { colour, secondaryButtonSx } from '../admin/tokens';

/**
 * Putting a family's confirmation right: rebuild it from what is stored, and — separately, behind
 * its own confirmation — email it to the family again.
 *
 * Two actions, never one. Rebuilding must not email anybody: an accidental mail-out to families is
 * what this is designed against. So the email sits apart from the rebuild, opens a dialog that names
 * the address it will go to, says plainly when that is the real family, and has nothing pre-selected.
 *
 * For the accounts team (and anyone on both teams) who is not view-only. The server checks again.
 */

const when = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('en-AU', {
        day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
};

export const mayFixConfirmations = () => seesAmounts() && getAdminAccessLevel() !== 'view';

const ConfirmationActions = ({ document = {}, parentId, dataLogId, onRegenerated }) => {
    const [regenerated, setRegenerated] = useState({ at: document.regeneratedAt || null, by: document.regeneratedBy || '' });
    const [busy, setBusy] = useState('');
    const [message, setMessage] = useState(null);
    const [confirming, setConfirming] = useState(false);
    // A second click while the first is still running would rebuild again and archive the file the
    // first one just made. State updates are not immediate enough to stop that; a ref is.
    const running = useRef(false);

    if (!mayFixConfirmations()) return null;

    const regenerate = async () => {
        if (running.current) return;
        running.current = true;
        setBusy('regenerate');
        setMessage(null);
        try {
            const result = await regenerateConfirmation({ parentId, dataLogId });
            setRegenerated({ at: result.regeneratedAt || new Date().toISOString(), by: result.regeneratedBy || '' });
            setMessage({ ok: true, text: 'Rebuilt. The previous file is kept in the archive. Nobody was emailed.' });
            if (onRegenerated) await onRegenerated(result);
        } catch (error) {
            setMessage({ ok: false, text: error?.response?.data?.error || 'Could not rebuild the confirmation.' });
        } finally {
            running.current = false;
            setBusy('');
        }
    };

    const send = async () => {
        if (running.current) return;
        running.current = true;
        setBusy('email');
        setMessage(null);
        try {
            const result = await emailConfirmation({ parentId, dataLogId });
            const to = [].concat(result.sentTo || document.emailTo || []).join(', ');
            setConfirming(false);
            setMessage({ ok: true, text: `Confirmation emailed to ${to}.` });
        } catch (error) {
            setMessage({ ok: false, text: error?.response?.data?.error || 'Could not email the confirmation.' });
        } finally {
            running.current = false;
            setBusy('');
        }
    };

    const exists = document.exists !== false;
    const emailTo = document.emailTo || '';
    const reachesFamily = document.emailReachesFamily === true;

    return (
        <Box sx={{ width: '100%', mt: 1, pt: 1, borderTop: `1px dashed ${colour.border}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ flex: 1, fontSize: '12.5px', color: colour.muted, minWidth: 180 }}>
                    {regenerated.at
                        ? `Last rebuilt ${when(regenerated.at)}${regenerated.by ? ` by ${regenerated.by}` : ''}`
                        : exists ? 'As first sent to the family' : 'No confirmation has been stored for this re-enrolment'}
                </Typography>
                <Button size="small" sx={secondaryButtonSx} disabled={Boolean(busy)} onClick={regenerate}>
                    {busy === 'regenerate' ? <CircularProgress size={14} /> : 'Rebuild PDF'}
                </Button>
                {exists && (
                    <Button
                        size="small"
                        sx={secondaryButtonSx}
                        disabled={Boolean(busy) || !emailTo}
                        onClick={() => { setMessage(null); setConfirming(true); }}
                    >
                        Email to family…
                    </Button>
                )}
            </Box>
            {exists && !emailTo && (
                <Typography sx={{ fontSize: '12px', color: colour.muted, mt: 0.5 }}>
                    There is no email address on file to send it to.
                </Typography>
            )}
            {message && (
                <Alert severity={message.ok ? 'success' : 'error'} sx={{ mt: 1, borderRadius: '10px' }}>{message.text}</Alert>
            )}

            <Dialog open={confirming} onClose={() => busy !== 'email' && setConfirming(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Email the confirmation again?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '14px', color: colour.body, mb: 1.5 }}>
                        The current confirmation will be sent to:
                    </Typography>
                    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: colour.ink, wordBreak: 'break-all', mb: 1.5 }}>
                        {emailTo}
                    </Typography>
                    {reachesFamily ? (
                        <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                            This is the family&rsquo;s real address. They will receive it as soon as you send.
                        </Alert>
                    ) : (
                        <Alert severity="info" sx={{ borderRadius: '10px' }}>
                            This environment sends to the test inbox above, not to the family.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirming(false)} disabled={busy === 'email'} sx={{ textTransform: 'none', fontWeight: 700, color: colour.bodySoft }}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={send} disabled={busy === 'email'} sx={{ textTransform: 'none', fontWeight: 700 }}>
                        {busy === 'email' ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : `Send to ${emailTo}`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ConfirmationActions;
